import '@amzn/awsui-global-styles/polaris.css';
import './App.css';

import { feedbackClient, mpaClient, pmsClient } from '@amzn/awscat-portfolio-assessment-service-client';
import { AuthContext, FlashProvider as FlashMessagesProvider } from '@amzn/awscat-react-components';
import { AppLayout, AppLayoutProps, NonCancelableCustomEvent, SpaceBetween, SplitPanel } from '@amzn/awsui-components-react';
import React from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter as Router, Link } from 'react-router-dom';

import authentication from './common/Authentication';
import { DEFAULT_SPLIT_PANEL_SIZE, getSplitPanelI18NStrings, HelpPanelTopicOptions }  from './common/Constants';
import awsLogo from './common/images/aws-logo.png';
import { constants } from './common/Localization';
import { getLocale } from './common/LocalizationHelper';
import Breadcrumbs from './components/common/Breadcrumbs';
import SidePanel from './components/common/SidePanel';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { GuidedTourContextProvider } from './contexts/GuidedTourContext';
import { HelpPanelContext, HelpPanelContextData } from './contexts/HelpPanelContext';
import { iNavigationContext, NavigationContext } from './contexts/NavigationContext';
import { PortfolioProvider } from './contexts/PortfolioContext';
import { RecentPortfoliosProvider } from './contexts/RecentPortfoliosContext';
import { RumContextProvider } from './contexts/RumContext';
import { ISplitPanelContext, SplitPanelContext } from './contexts/SplitPanelContext';
import FlashMessages from './flash/FlashMessages';
import Footer from './Footer';
import AppHeader from './Header';
import Navigation from './Navigation';
import Routes from './Routes';

function App() {

    mpaClient.setAuthentication(authentication);
    feedbackClient.setAuthentication(authentication);
    pmsClient.setAuthentication(authentication);

    const [navigationOpen, setNavigationOpen] = React.useState(false);
    const [toolsOpen, setToolsOpen] = React.useState(false);
    const [toolsHide, setToolsHide] = React.useState(true);
    const [helpPanelTopic, setHelpPanelTopic] = React.useState(HelpPanelTopicOptions.DEFAULT);
    const title = (
        <Link to="/">
            <SpaceBetween size='s' direction='horizontal'>
                <img src={awsLogo} height='20px' id='title-image'/>
                <span>{constants.AWS_PORTFOLIO_ASSESSMENT_TITLE}</span>
            </SpaceBetween>
        </Link>
    );
    const [isAuthenticated, setAuthenticated] = React.useState(false);
    const [ splitPanelSize, setSplitPanelSize ] = React.useState(DEFAULT_SPLIT_PANEL_SIZE);
    const [ isSplitPanelOpen, setIsSplitPanelOpen ] = React.useState(false);
    const [ splitPanelContent, setSplitPanelContent ] = React.useState<JSX.Element | undefined>();

    authentication.setCallback((authenticated: boolean) => {
        setAuthenticated(authenticated);
    });

    const hideTools = (): void => {
        setToolsHide(true);
    };

    const openTools = (): void => {
        setToolsOpen(true);
    };

    const closeTools = (): void => {
        setToolsOpen(false);
    };

    const onToolChange = (event: NonCancelableCustomEvent<AppLayoutProps.ChangeDetail>): void => {
        setToolsOpen(event.detail.open);
    };

    const changePanelTopic = (topic: HelpPanelTopicOptions) => {
        setHelpPanelTopic(topic);
        setToolsHide(false);
    };

    const helpPanelCxt: HelpPanelContextData = {
        panelHandler: changePanelTopic,
        panelOpen: openTools,
        panelTopic: helpPanelTopic,
        isPanelOpen: toolsOpen,
        panelHidden: hideTools,
        panelClose: closeTools,
    };

    const navigationCxt: iNavigationContext = {
        setNavigationOpen: () => setNavigationOpen(true),
        setNavigationClosed: () => setNavigationOpen(false)
    };
    
    const onSplitPanelContentChanged = (body: JSX.Element | undefined, header: string) => {
        body ? setSplitPanelContent(
            <SplitPanel
                header={header}
                i18nStrings={getSplitPanelI18NStrings()}
                hidePreferencesButton
            >
                {body}
            </SplitPanel>
        ) : setSplitPanelContent(undefined);
    };

    const splitPanelCxt: ISplitPanelContext = {
        openPanel: () => setIsSplitPanelOpen(true),
        closePanel: () => setIsSplitPanelOpen(false),
        panelContent: splitPanelContent,
        setPanelContent: onSplitPanelContentChanged
    };

    return (
        <Router>
            <div id='b'>
                <FlashMessagesProvider>
                    <AuthContext.Provider value={{ auth: authentication }}>
                        <IntlProvider locale={getLocale()}>
                            <CookieConsentProvider>
                                <RecentPortfoliosProvider>
                                    <SplitPanelContext.Provider value={splitPanelCxt}>
                                        <NavigationContext.Provider value={navigationCxt}>
                                            <HelpPanelContext.Provider value={helpPanelCxt}>
                                                <RumContextProvider>
                                                    <GuidedTourContextProvider>
                                                        <PortfolioProvider>
                                                            <AppHeader isAuthenticated={isAuthenticated} title={title}/>
                                                            <div id="app">
                                                                <AppLayout
                                                                    content={<Routes />}
                                                                    breadcrumbs={<Breadcrumbs isAuthenticated={isAuthenticated} />}
                                                                    footerSelector={'#b #page-footer'}
                                                                    headerSelector={'#b #h'}
                                                                    navigation={<Navigation />}
                                                                    navigationOpen={navigationOpen}
                                                                    onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
                                                                    notifications={
                                                                        <SpaceBetween size='xxs'>
                                                                            <FlashMessages />
                                                                        </SpaceBetween>
                                                                    }
                                                                    stickyNotifications={true}
                                                                    splitPanelOpen={isSplitPanelOpen}
                                                                    splitPanel={splitPanelContent}
                                                                    splitPanelSize={splitPanelSize}
                                                                    onSplitPanelResize={({ detail }) => setSplitPanelSize(detail.size)}
                                                                    onSplitPanelToggle={({ detail }) => setIsSplitPanelOpen(detail.open)}
                                                                    tools={<SidePanel helpPanelTopic={helpPanelTopic} />}
                                                                    toolsHide={toolsHide}
                                                                    toolsOpen={toolsOpen}
                                                                    onToolsChange={onToolChange}
                                                                />
                                                            </div>
                                                            <Footer />
                                                        </PortfolioProvider>
                                                    </GuidedTourContextProvider>
                                                </RumContextProvider>
                                            </HelpPanelContext.Provider>
                                        </NavigationContext.Provider>
                                    </SplitPanelContext.Provider>
                                </RecentPortfoliosProvider>
                            </CookieConsentProvider>
                        </IntlProvider>
                    </AuthContext.Provider>
                </FlashMessagesProvider>
            </div>
        </Router>
    );
}

export default App;
